/**
 * 🎯 DISCOVERY FACTORY: Centralized ESG Asset Orchestrator
 * Location: src/registered/common/parsers/DiscoveryFactory.js
 */
import { XlsxController } from './XlsxController';
import { CsvController } from './CsvController';
import { JsonController } from './JsonController';
import { XmlController } from './XmlController';

const ParserRegistry = {
    xlsx: XlsxController,
    xls: XlsxController,
    csv: CsvController,
    json: JsonController,
    xml: XmlController,
    xbrl: XmlController
};

export const DiscoveryFactory = {
    /**
     * 🚀 RUN PIPELINE
     * Waterfall logging to catch silent hang-ups during stream or handoff.
     */
    run: async (fileUrl, fileName, onProgress) => {
        const timestamp = new Date().toISOString();
        console.group(`🔥 [DISCOVERY_START] ${timestamp}`);
        console.log("📍 URL:", fileUrl);
        console.log("📄 FILE:", fileName);
        try {
            // STAGE 1: FETCH
            onProgress({ stage: 'DOWNLOAD', status: 'IN_PROGRESS', percent: 25, label: 'Streaming...' });
            
            console.log("📡 [STAGE 1] Initiating fetch request...");
            const response = await fetch(fileUrl);
            
            if (!response.ok) {
                const errorMsg = `Network Fail: ${response.status} ${response.statusText}`;
                console.error(`❌ [STAGE 1] ${errorMsg}`);
                throw new Error(errorMsg);
            }

            console.log("📡 [STAGE 1] Stream connected. Converting to ArrayBuffer...");
            const buffer = await response.arrayBuffer();
            console.log(`📡 [STAGE 1] Success. Memory allocated: ${buffer.byteLength} bytes.`);

            onProgress({ stage: 'DOWNLOAD', status: 'SUCCESS', percent: 40, label: 'RAM check pass.' });

            // STAGE 2: RESOLUTION
            const ext = fileName.split('.').pop().toLowerCase();
            const parser = ParserRegistry[ext];
            
            console.log(`🧭 [STAGE 2] Extension detected: .${ext}`);
            
            if (!parser) {
                console.error(`❌ [STAGE 2] Unsupported format: ${ext}`);
                throw new Error(`Format Discovery Failure: '${ext}' is not supported.`);
            }

            console.log(`🧭 [STAGE 2] Handoff to: ${parser.constructor?.name || 'Assigned Controller'}`);

            onProgress({ 
                stage: 'OPEN_FILE', 
                status: 'IN_PROGRESS', 
                percent: 50, 
                label: `Handoff to ${ext.toUpperCase()} parser...` 
            });

            // STAGE 3: EXECUTION
            console.log("🛠️ [STAGE 3] Entering Parser.execute()...");
            console.groupEnd(); // End factory group before parser starts its own

            // 🎯 PASS INJECTED CONFIG AS SECOND ARGUMENT
            const result = await parser.execute(buffer, onProgress);
            
            console.log("🏆 [DISCOVERY_COMPLETE] Pipeline returned result successfully.");
            return result;

        } catch (err) {
            console.error("💀 [DISCOVERY_CRITICAL_FAIL]", err);
            console.groupEnd();
            onProgress({ status: 'FAIL', label: err.message, percent: 100 });
            throw err;
        }
    }
};